/**
 * FICHE CONTACT v3 (#208, revue) — AFFICHER un numéro.
 *
 * La fiche montrait `+33655000003` d'un bloc : illisible à dicter ou à
 * comparer. Ce module ne fait QUE de l'affichage :
 *
 *   - il ne valide rien et ne normalise rien — le serveur reste seul juge
 *     (`normalizeContactPhone`) ; le numéro COMPOSÉ est toujours la valeur
 *     enregistrée, jamais ce texte ;
 *   - il ne retire ni n'ajoute aucun chiffre : seuls des espaces (insécables,
 *     pour qu'un numéro ne se coupe pas en fin de ligne) sont glissés entre
 *     les groupes ;
 *   - il ne découpe que ce qu'il CONNAÎT (France, outre-mer, Israël, Amérique
 *     du Nord — les numéros que le CRM rencontre). Tout le reste est rendu tel
 *     quel : mieux vaut un numéro compact qu'un découpage inventé.
 *
 * Module pur, sans i18n ni dépendance : pas de bibliothèque de numérotation
 * côté écran (elle pèserait sur la PWA pour quatre découpages).
 */

const NBSP = ' '

interface Layout {
  /** Indicatif, sans le `+`. */
  callingCode: string
  /** Nombre de chiffres du numéro national (après l'indicatif). */
  nationalLength: number
  /** Taille des groupes du numéro national, dans l'ordre. */
  groups: number[]
}

/** Outre-mer d'abord : `+262…` ne doit pas être pris pour un autre plan. */
const LAYOUTS: Layout[] = [
  ...['262', '590', '594', '596', '508'].map((callingCode) => ({
    callingCode,
    nationalLength: 9,
    groups: [3, 2, 2, 2],
  })),
  { callingCode: '33', nationalLength: 9, groups: [1, 2, 2, 2, 2] },
  { callingCode: '972', nationalLength: 9, groups: [2, 3, 4] },
  { callingCode: '972', nationalLength: 8, groups: [1, 3, 4] },
  { callingCode: '1', nationalLength: 10, groups: [3, 3, 4] },
]

/** `+33612345601` → chiffres après le `+`, ou `null` si ce n'est pas un numéro international. */
function internationalDigits(phone: string | null | undefined): string | null {
  if (!phone) return null
  const match = /^\+(\d{6,15})$/.exec(phone.trim())
  return match ? match[1] : null
}

/**
 * Le numéro, lisible : `+33 6 12 34 56 01`. Rendu TEL QUEL s'il n'est pas un
 * numéro international ou si son plan n'est pas connu de l'écran.
 */
export function formatPhoneForDisplay(phone: string | null | undefined): string {
  if (!phone) return ''
  const digits = internationalDigits(phone)
  if (!digits) return phone
  const layout = LAYOUTS.find(
    (candidate) =>
      digits.startsWith(candidate.callingCode) &&
      digits.length === candidate.callingCode.length + candidate.nationalLength,
  )
  if (!layout) return phone
  const national = digits.slice(layout.callingCode.length)
  const parts: string[] = []
  let at = 0
  for (const size of layout.groups) {
    parts.push(national.slice(at, at + size))
    at += size
  }
  return [`+${layout.callingCode}`, ...parts].join(NBSP)
}

export type PhoneDisplayKind = 'MOBILE' | 'FIXED'

/** Préfixes nationaux des territoires d'outre-mer (plan de numérotation français). */
const OVERSEAS: Record<string, { mobile: string[]; fixed: string[] }> = {
  '262': { mobile: ['692', '693', '639'], fixed: ['262', '263', '269'] },
  '590': { mobile: ['690', '691'], fixed: ['590'] },
  '594': { mobile: ['694'], fixed: ['594'] },
  '596': { mobile: ['696', '697'], fixed: ['596'] },
}

/**
 * Mobile ou fixe, quand le NUMÉRO le dit de lui-même (plan de numérotation
 * français : 06/07 mobiles, 01 à 05 fixes ; préfixes d'outre-mer). Aucun appel
 * réseau, aucune base. `null` dès qu'il y a doute (09, 08, autre pays, valeur
 * libre) : l'écran n'affiche alors AUCUNE étiquette plutôt qu'une supposition.
 */
export function phoneKindOf(phone: string | null | undefined): PhoneDisplayKind | null {
  const digits = internationalDigits(phone)
  if (!digits) return null
  for (const [callingCode, prefixes] of Object.entries(OVERSEAS)) {
    if (digits.startsWith(callingCode) && digits.length === callingCode.length + 9) {
      const prefix = digits.slice(callingCode.length, callingCode.length + 3)
      if (prefixes.mobile.includes(prefix)) return 'MOBILE'
      if (prefixes.fixed.includes(prefix)) return 'FIXED'
      return null
    }
  }
  if (digits.startsWith('33') && digits.length === 11) {
    const zone = digits[2]
    if (zone === '6' || zone === '7') return 'MOBILE'
    if ('12345'.includes(zone)) return 'FIXED'
  }
  return null
}
